import React from 'react';
import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useTerminology } from '../../hooks/useTerminology';
export type WidgetState = 'loading' | 'ready' | 'empty' | 'error';
interface WidgetProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
  state?: WidgetState;
  /** Shown when `state` is `empty`. Says what is absent, never a fake number. */
  emptyTitle?: string;
  emptyDescription?: string;
  /** Shown when `state` is `error`, alongside a retry when one is possible. */
  errorMessage?: string;
  onRetry?: () => void;
  /** Provenance or a limit of the measurement. Rendered under the content. */
  footnote?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * One dashboard widget. Every widget declares its own state, because a
 * dashboard where a single failing panel blanks the page is worse than no
 * dashboard: the administrator cannot tell what is broken from what is empty.
 *
 * `empty` and `error` are deliberately different. Empty means the platform has
 * nothing to report and that is the truth; error means we do not know, and
 * saying nothing would read as zero.
 */
export function Widget({
  title,
  description,
  action,
  state = 'ready',
  emptyTitle = 'No data available',
  emptyDescription,
  errorMessage,
  onRetry,
  footnote,
  className = '',
  children
}: WidgetProps) {
  // Widget headings, empty states and footnotes name business concepts as often
  // as navigation does, so they resolve terminology templates too. Plain text
  // passes through unchanged, so no existing widget needed editing.
  const {
    phrase
  } = useTerminology();
  return <section className={`flex min-w-0 flex-col rounded-card border border-line bg-surface ${className}`} aria-busy={state === 'loading' || undefined} data-id="element-458" data-mp="31fjfmw3i1eju">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3.5" data-id="element-459" data-mp="3p3ar5xh7tnk2">
        <div className="min-w-0" data-id="element-460" data-mp="3txx4w7arrs7b">
          <h3 className="text-body font-semibold text-ink" data-id="element-461" data-mp="7mye7t5lksk1">{phrase(title)}</h3>
          {description && <p className="mt-0.5 max-w-[52ch] text-caption leading-5 text-ink-muted" data-id="element-462" data-mp="2on4difayi1t">
              {phrase(description)}
            </p>}
        </div>
        {action && state === 'ready' && <div className="shrink-0" data-id="element-463" data-mp="3ovlbyayxrdhl">{action}</div>}
      </header>

      <div className="min-w-0 flex-1 px-4 py-4" data-id="element-464" data-mp="3kt87dpvxdlme">
        {state === 'loading' && <WidgetSkeleton data-id="element-465" data-mp="3kujbxrx72xek" />}

        {state === 'error' && <div className="flex flex-col items-start gap-3" data-id="element-466" data-mp="3g28ecvpxfnp6">
            <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-467" data-mp="3b3ew6pzp38io">
              <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-danger-700" aria-hidden="true" data-id="element-468" data-mp="3b3oncgwh7lpy" />
              <span data-id="element-469" data-mp="315r3s6pkad3o">{errorMessage ?? 'This panel could not be loaded.'}</span>
            </p>
            {onRetry && <Button variant="secondary" size="sm" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-471" data-mp="1ckjvs5nxnupl" />} onClick={onRetry} data-id="element-470" data-mp="2x4xcr4gtoi6k">
                Try again
              </Button>}
          </div>}

        {state === 'empty' && <div className="py-2" data-id="element-472" data-mp="1lmt7gyuxtu4k">
            <p className="text-body-sm font-medium text-ink" data-id="element-473" data-mp="1qlcqori5crt8">{phrase(emptyTitle)}</p>
            {emptyDescription && <p className="mt-1 max-w-[52ch] text-caption leading-5 text-ink-muted" data-id="element-474" data-mp="sy49v9t3c6xk">
                {phrase(emptyDescription)}
              </p>}
          </div>}

        {state === 'ready' && children}
      </div>

      {footnote && state === 'ready' && <p className="border-t border-line px-4 py-2.5 text-caption leading-5 text-ink-subtle" data-id="element-475" data-mp="17miffshw69ht">
          {phrase(footnote)}
        </p>}
    </section>;
}
function WidgetSkeleton() {
  return <div className="space-y-2.5" aria-hidden="true" data-id="element-476" data-mp="f5xv9s5l6808">
      {[70, 92, 55].map((width, index) => <div key={index} className="cz-skeleton h-4 rounded bg-canvas" style={{
      width: `${width}%`
    }} data-id="element-477" data-mp="2gv3r8kvu7e1j" data-mpi={index} />)}
    </div>;
}
interface BoundaryProps {
  title: string;
  children: React.ReactNode;
}

/**
 * Contains a widget's failure to that widget. Without this, one thrown render
 * takes the whole console down — which is the opposite of what a monitoring
 * surface is for.
 */
export class WidgetBoundary extends React.Component<BoundaryProps, {
  failed: boolean;
}> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = {
      failed: false
    };
  }
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch(error: unknown) {
    // Recorded for the operator, not swallowed.
    console.error('[admin widget]', this.props.title, error);
  }
  render() {
    if (this.state.failed) {
      return <Widget title={this.props.title} state="error" errorMessage="This panel stopped responding. The rest of the console is unaffected." onRetry={() => this.setState({
        failed: false
      })} data-id="element-478" data-mp="1a6y9dnrjw742" />;
    }
    return this.props.children;
  }
}